import { useSearchParams } from 'react-router-dom';
import { IErrRes, IFilterDataDB } from '../../type/db';
import { IFilter } from 'apis/match_apis';
import { codeToName } from '../../utils/match/matchFilterData';
import { apis } from 'apis/apis';
import {
	QueryFunction,
	QueryKey,
	useQuery,
	useQueryClient,
	UseQueryOptions,
	UseQueryResult,
} from 'react-query';
import { IIgFilter, INbFilter, IYtFilter, ITtFilter } from 'store/type';
import useCommonStore from 'store/useCommonStore';
import { PlatformCode } from 'type/common';
import useAuthOver from 'hooks/useAuthOver';
import { AxiosRequestConfig } from 'axios';
import { api } from 'src/apis/api';
// 찾기 필터
export const useGetFilterQuery = <T>(options?: T) => {
	const { platform, setFilter, filter } = useCommonStore();
	const platform_code = platform.code as PlatformCode;

	return useQuery(['match_filter', platform.code], () => apis.getFilter(platform_code), {
		retry: 0,
		onSuccess(data) {
			const data_list = data.filter_info;
			const key_filter = Object.keys(filter[`${platform_code}_filter`]);

			// 받아온 데이터에서 key_filter에 있는 항목들만 필터링
			const filtered_data_list = Object.entries(data_list)
				.filter((item) => key_filter.includes(item[0]))
				// null을 all로 대치
				.map((item) => {
					if (item[1] === null) item[1] = 'all';
					return item;
				})
				// db데이터(code)를 텍스트로 대치
				.map((item) => {
					item[1] = codeToName(platform_code, item[0], item[1] as string);
					return item;
				});

			const result_data_list = Object.fromEntries(filtered_data_list);

			setFilter(
				platform.code ?? 'ig',
				result_data_list as IIgFilter & IYtFilter & INbFilter & ITtFilter,
			);
		},
		onError: (err) => {
			console.log(err);
		},
		enabled: platform.code !== null ? true : false,
		staleTime: 60 * 60 * 1000,
		cacheTime: Infinity,
		...options,
	});
};

// 찾기 리스트
export const useGetMatchListQuery = <T>(options?: T) => {
	const { page, filter, platform } = useCommonStore();
	const platform_code = platform.code as PlatformCode;

	const [authOverCondition, setAuthOverError] = useAuthOver();

	const [search_params] = useSearchParams();
	const page_query = search_params.get('page') ?? '0';

	const queryClient = useQueryClient();

	const filter_data: IFilterDataDB | undefined = queryClient.getQueryData([
		'match_filter',
		platform_code,
	]);
	const params = filter_data?.filter_info as IFilter;

	const obj = { ...params, ...filter.common_filter, page: page.toString() };

	return useQuery(
		['match_list', new URLSearchParams(obj).toString()],
		() => apis.getMatchList(obj),
		{
			retry: 0,
			enabled:
				params && params.page_size && filter_data && page === +page_query ? true : false,
			onError: (err: IErrRes) => {
				if (authOverCondition(err)) {
					setAuthOverError();
				}
			},
			staleTime: 3600000,
			cacheTime: Infinity,
			...options,
		},
	);
};

// 프리뷰
export const useGetPreviewQuery = <T>(
	platform_pk: string,
	visible: boolean,
	options?: T,
) => {
	const { platform } = useCommonStore();
	const platform_code = platform.code as PlatformCode;

	return useQuery(
		['influencer_preview', platform, platform_pk],
		() => apis.getPreview(platform_code, platform_pk),
		{
			retry: 0,
			onError: (err) => {
				console.log(err);
			},
			enabled: platform_code !== null && platform_pk !== '' && visible,
			staleTime: 1000 * 60 * 60,
			cacheTime: Infinity,
			...options,
		},
	);
};

type AwaitedInput<T> = PromiseLike<T> | T;
type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;
type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

export type CategoryDetail = {
	/**
	 * @maxLength 50
	 * @nullable
	 */
	en_name: string | null;
	/**
	 * @minLength 1
	 * @maxLength 50
	 */
	id: string;
	/**
	 * @maxLength 50
	 * @nullable
	 */
	ja_name: string | null;
	/**
	 * @minLength 1
	 * @maxLength 50
	 */
	name: string;
};

export type CategoryListGetRes = {
	categories?: CategoryDetail[];
	/** @minLength 1 */
	platform_code: string;
	platform_type?: number;
};

/**
 * report-aurora-cluster DB 테이블에 정의되어있는 플랫폼별 카테고리 리스트를 반환하는 API
 * @summary 카테고리 리스트 조회 (CategoryListGet)
 */
export const categoryListGet = (
	platformCode: string,
	signal?: AbortSignal,
): Promise<CategoryListGetRes> => {
	const promise = api({
		url: `/categories/${platformCode}/list/`,
		method: 'GET',
		signal,
	}).then(({ data }) => data.result);

	return promise;
};

export const getCategoryListGetQueryKey = (platformCode: string) => {
	return [`/categories/${platformCode}/list/`] as const;
};

export const useCategoryListGetQueryOptions = <
	TData = Awaited<ReturnType<typeof categoryListGet>>,
	TError = unknown,
>(
	platformCode: string,
	options?: UseQueryOptions<Awaited<ReturnType<typeof categoryListGet>>, TError, TData>,
) => {
	const queryKey = options?.queryKey ?? getCategoryListGetQueryKey(platformCode);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof categoryListGet>>> = ({
		signal,
	}) => categoryListGet(platformCode, signal);

	const customOptions = {
		staleTime: 60 * 1000,
		cacheTime: 3 * 60 * 1000,
		...options,
		queryKey,
		queryFn,
	};

	return customOptions as UseQueryOptions<
		Awaited<ReturnType<typeof categoryListGet>>,
		TError,
		TData
	> & {
		queryKey: QueryKey;
	};
};

/**
 * @summary 카테고리 리스트 조회 (CategoryListGet)
 */
export const useCategoryListGet = <
	TData = Awaited<ReturnType<typeof categoryListGet>>,
	TError = unknown,
>(
	platformCode: string,
	options?: UseQueryOptions<Awaited<ReturnType<typeof categoryListGet>>, TError, TData>,
): UseQueryResult<TData, TError> & {
	queryKey: QueryKey;
} => {
	const queryOptions = useCategoryListGetQueryOptions(platformCode, options);

	const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
		queryKey: QueryKey;
	};

	query.queryKey = queryOptions.queryKey;

	return query;
};
